/**
 * Sidebar panel + HUD numbers, ported from the prototype's renderPanel().
 * Re-renders only when a state signature changes (rebuilding cards mid-hover
 * would break preview interactions); gold/lives update every frame cheaply.
 */
import {
  CHANCE_COSTS,
  CHANCE_TABLE,
  LAST_WAVE,
  SPECIALS,
  TIERS,
  TIER_COLORS,
} from '../data/balance.js';
import type { Game } from '../core/game.js';
import { waveDef } from '../core/waves.js';
import type { WaveDef, GemCell } from '../core/types.js';
import type { ViewState } from '../render/viewState.js';
import type { SlateKey, SpecialKey } from '../data/balance.js';
import { SLATES } from '../data/balance.js';
import { byId, clear, el } from './dom.js';
import { descFor, swatchFor } from './descriptions.js';
import { recipeProgress } from './recipes.js';
import { GEMS, TIERS as TIER_NAMES } from '../data/balance.js';

export interface PanelCallbacks {
  onKeep(i: number): void;
  onCombine(i: number, need: number): void;
  onRecipe(i: number, key: SpecialKey): void;
  /** Toggle the socket-picker for forging slate `key` from card `i`. */
  onSlateForge(i: number, key: SlateKey): void;
  onRetire(): void;
  onUpgrade(): void;
  onPreviewKeep(i: number): void;
  onPreviewCombine(i: number, need: number): void;
  onPreviewRecipe(i: number, key: SpecialKey): void;
  onPreviewSlate(i: number, key: SlateKey): void;
  onClearPreview(): void;
  onRestart(): void;
}

const PHASE_TITLES: Record<string, string> = {
  build: 'Build · place stones',
  choose: 'Pick your gem',
  prewave: 'Wave incoming...',
  gameover: 'Game over',
};

function chipFor(tag: string): HTMLSpanElement {
  const map: Record<string, string> = {
    'FAST!': '#ff9e3d',
    fast: '#d9a45b',
    FLYING: '#d78cff',
    BOSS: '#ff6b7a',
    'MEGA BOSS': '#ff5470',
  };
  const col = map[tag] ?? '#8a92a6';
  return el('span', {
    className: 'chip',
    text: tag.replace('!', ''),
    style: { background: `${col}22`, color: col, border: `1px solid ${col}55` },
  });
}

function chipsFor(d: WaveDef): HTMLSpanElement[] {
  return d.tags.map(chipFor);
}

function statline(game: Game, g: GemCell): HTMLElement {
  const st = game.gemStats(g);
  const abColor = g.special !== undefined ? SPECIALS[g.special].color : st.color;
  return el('div', { className: 'statline' }, [
    el('span', { className: 'stat', text: `⚔ ${st.dmg}` }),
    el('span', { className: 'stat', text: `◎ ${st.range.toFixed(1)}` }),
    el('span', { className: 'stat', text: `⚡ ${(1 / st.cd).toFixed(1)}/s` }),
    el('div', { className: 'ability', text: descFor(g), style: { color: abColor } }),
  ]);
}

export class Panel {
  private signature = '';

  constructor(
    private readonly view: ViewState,
    private readonly cb: PanelCallbacks,
  ) {}

  /** Cheap per-frame HUD updates. */
  tick(game: Game): void {
    byId('gold').textContent = String(game.gold);
    byId('lives').textContent = String(game.lives);
  }

  /** Full re-render when the signature changes. */
  render(game: Game): void {
    const upAffordable =
      game.chanceLvl < CHANCE_COSTS.length && game.gold >= CHANCE_COSTS[game.chanceLvl]!;
    const sig = [
      game.phase,
      game.wave,
      game.chanceLvl,
      game.newGems.length,
      game.newGems.map((g) => `${g.special ?? g.type}:${g.tier ?? ''}`).join(','),
      game.lifted
        ? `held:${game.lifted.kind === 'gem' ? (game.lifted.special ?? game.lifted.type) : 'rock'}`
        : '',
      upAffordable,
      this.view.replayMode,
      game.won,
      this.view.pendingSlate ? `${this.view.pendingSlate.i}:${this.view.pendingSlate.key}` : '',
      this.view.pendingSlateUp ? `${this.view.pendingSlateUp.x},${this.view.pendingSlateUp.y}` : '',
    ].join('|');
    if (sig === this.signature) return;
    this.signature = sig;

    byId('waveNo').textContent = String(game.wave);
    byId('waveMax').textContent = game.wave > LAST_WAVE ? ' endless' : '/50';
    const title =
      game.phase === 'wave' ? `Wave ${game.wave}` : (PHASE_TITLES[game.phase] ?? game.phase);
    byId('phaseHead').textContent = title;

    this.renderPhaseBody(game);
    this.renderWavePreview(game);
    this.renderChances(game);
  }

  private renderPhaseBody(game: Game): void {
    const body = byId('phaseBody');
    clear(body);
    if (game.lifted && (game.phase === 'build' || game.phase === 'choose')) {
      const held = game.lifted;
      const label =
        held.kind !== 'gem' // rocks only: slates can never be lifted
          ? 'Rock'
          : held.special !== undefined
            ? game.gemStats(held).name
            : `${TIER_NAMES[held.tier]!} ${GEMS[held.type].name}`;
      const note = el('p', { className: 'phase-note holding-note' }, [
        'Holding: ',
        el('b', { text: label, style: { color: '#ffd257' } }),
        el('br'),
        el('small', { text: 'Place it on an empty cell before the wave starts.' }),
      ]);
      body.append(note);
    }
    if (game.phase === 'build') {
      const flying = waveDef(game.wave).flying;
      body.append(
        el('p', { className: 'phase-note' }, [
          'Place your stones: ',
          el('b', { text: `${game.newGems.length}/5`, style: { color: '#fff' } }),
          el('br'),
          el('small', {
            text:
              'Click an empty cell to place · click a fresh stone to pick it back up. ' +
              `Blue line = creep route${flying ? ' · purple dashes = flight path' : ''}.`,
          }),
        ]),
      );
      if (this.view.pendingSlateUp) {
        body.append(
          el('p', { className: 'phase-note' }, [
            el('b', { text: 'Greater slate: ', style: { color: '#ffd257' } }),
            el('small', {
              text: 'tap a glowing partner slate to combine · tap elsewhere to cancel.',
            }),
          ]),
        );
      }
      if (game.won && !game.recording.result && !this.view.replayMode) {
        const retire = el('button', { text: '🏁 Retire & submit run' });
        retire.addEventListener('click', () => {
          this.cb.onRetire();
        });
        body.append(
          el('p', { className: 'phase-note' }, [
            el('small', {
              text: 'Endless: each wave grows 5% stronger. Retire any time to bank your record. ',
            }),
            retire,
          ]),
        );
      }
    } else if (game.phase === 'choose') {
      if (this.view.pendingSlate) {
        body.append(
          el('p', { className: 'phase-note' }, [
            el('b', {
              text: `Socket the ${SLATES[this.view.pendingSlate.key].name}: `,
              style: { color: SLATES[this.view.pendingSlate.key].color },
            }),
            el('small', { text: 'tap a glowing rock · tap the button again to cancel.' }),
          ]),
        );
      }
      body.append(
        el('p', {
          className: 'recipe-legend',
          text: '\u2605 recipe \u00b7 its 3 parts light up as you own them \u00b7 hover for names',
        }),
      );
      game.newGems.forEach((g, i) => {
        body.append(this.buildCard(game, g, i));
      });
    } else if (game.phase === 'wave' || game.phase === 'prewave') {
      const def = waveDef(game.wave);
      const stats = el('p', { className: 'phase-note' }, [
        `${def.count} creep${def.count > 1 ? 's' : ''}`,
        el('span', {
          className: 'stat',
          text: `♥ ${def.hp}`,
          style: { color: '#ff8c8c', marginLeft: '8px' },
        }),
      ]);
      if (def.armor) stats.append(el('span', { className: 'stat', text: `🛡 ${def.armor}` }));
      const chips = el('div', { style: { marginTop: '6px', marginLeft: '-4px' } });
      const chipEls = chipsFor(def);
      if (chipEls.length) chips.append(...chipEls);
      else
        chips.append(
          el('span', { text: 'No modifiers', style: { color: '#8a92a6', fontSize: '12px' } }),
        );
      body.append(stats, chips);
    } else {
      const restart = el('button', { text: 'Restart' });
      restart.addEventListener('click', () => {
        this.cb.onRestart();
      });
      const summary = game.won
        ? `Victory — reached wave ${game.recording.result?.wave ?? game.wave} in the endless waves. `
        : `Reached wave ${game.wave}. `;
      body.append(el('p', { text: summary }, [restart]));
    }
  }

  private buildCard(game: Game, g: GemCell, i: number): HTMLElement {
    const st = game.gemStats(g);
    const tierColor = g.tier !== undefined ? TIER_COLORS[g.tier]! : st.color;
    const card = el('div', { className: 'card', style: { borderLeftColor: st.color } });

    const withPreview = (btn: HTMLButtonElement, enter: () => void): HTMLButtonElement => {
      btn.addEventListener('mouseenter', enter);
      btn.addEventListener('mouseleave', () => {
        this.cb.onClearPreview();
      });
      return btn;
    };

    const btns: HTMLButtonElement[] = [];
    const keep = el('button', { className: 'primary', text: 'Keep' });
    keep.addEventListener('click', () => {
      this.cb.onKeep(i);
    });
    btns.push(
      withPreview(keep, () => {
        this.cb.onPreviewKeep(i);
      }),
    );

    if (g.tier !== undefined) {
      const same = game.identicalIn(game.combinePool(), g).length;
      const combineBtn = (need: number, jump: number): void => {
        const target = g.tier + jump;
        const b = el('button', { style: { borderColor: TIER_COLORS[target]! } }, [
          `Combine ${need} → `,
          el('span', { text: TIERS[target]!, style: { color: TIER_COLORS[target]! } }),
        ]);
        b.addEventListener('click', () => {
          this.cb.onCombine(i, need);
        });
        btns.push(
          withPreview(b, () => {
            this.cb.onPreviewCombine(i, need);
          }),
        );
      };
      if (g.tier < 5 && same >= 2) combineBtn(2, 1);
      if (g.tier < 4 && same >= 4) combineBtn(4, 2);
      for (const r of game.availableRecipes(g)) {
        const s = SPECIALS[r.key];
        const b = el('button', {
          text: `✦ ${s.name}!`,
          style: { borderColor: s.color },
        });
        b.addEventListener('click', () => {
          this.cb.onRecipe(i, r.key);
        });
        btns.push(
          withPreview(b, () => {
            this.cb.onPreviewRecipe(i, r.key);
          }),
        );
      }
      for (const r of game.availableSlates(g)) {
        const s = SLATES[r.key];
        const active = this.view.pendingSlate?.i === i && this.view.pendingSlate.key === r.key;
        const b = el('button', {
          text: `◆ ${s.name}${active ? ' — pick a rock…' : ''}`,
          style: { borderColor: s.color, ...(active && { color: s.color }) },
        });
        b.addEventListener('click', () => {
          this.cb.onSlateForge(i, r.key);
        });
        btns.push(
          withPreview(b, () => {
            this.cb.onPreviewSlate(i, r.key);
          }),
        );
      }
    }

    // Identical settled gems (same type + quality) already on the board --
    // the combine/redundancy question answered without looking away.
    const onBoard =
      game.identicalIn(game.combinePool(), g).length - game.identicalIn(game.newGems, g).length;
    card.append(
      el('div', { className: 'row' }, [
        el('span', { className: 'num', text: String(i + 1) }),
        swatchFor(g),
        el('b', { text: st.name, style: { color: tierColor } }),
        el('span', {
          className: `board-count${onBoard > 0 ? ' some' : ''}`,
          text: `×${onBoard}`,
          title: `${onBoard} identical gem${onBoard === 1 ? '' : 's'} (same type and quality) already on the board`,
        }),
      ]),
      statline(game, g),
      this.buildRecipeHints(game, g),
      el('div', { className: 'btns' }, btns),
    );
    card.addEventListener('mouseenter', () => {
      this.view.selectedCell = { x: g.x, y: g.y };
    });
    card.addEventListener('mouseleave', () => {
      this.view.selectedCell = null;
    });
    return card;
  }

  /**
   * The card's right-side recipe tracker: for every special this gem feeds,
   * its three components as mini swatches -- present in full color, missing
   * dimmed. Complete recipes already surface as forge buttons; the tracker's
   * job is showing what is still missing without opening the recipes modal.
   */
  private buildRecipeHints(game: Game, g: GemCell): HTMLElement {
    const stack = el('div', { className: 'recipes-hint' });
    for (const status of recipeProgress(game, g)) {
      const special = SPECIALS[status.key];
      const row = el('div', {
        className: `recipe-row${status.complete ? ' complete' : ''}`,
        title:
          `${special.name} - ${special.desc}. ` +
          (status.complete
            ? 'Ready to forge!'
            : 'Missing: ' +
              status.comps
                .filter((c) => !c.have)
                .map((c) => `${TIER_NAMES[c.tier]!} ${GEMS[c.type].name}`)
                .join(', ')),
      });
      row.append(
        el('span', {
          className: `sw star mini${status.complete ? '' : status.missing === 1 ? ' near' : ' dim-star'}`,
          style: { background: special.color },
        }),
      );
      for (const comp of status.comps) {
        // The tier-colored plate behind the shape is the component's quality
        // (clip-path swallows outlines/shadows, so a wrapper carries it).
        row.append(
          el(
            'span',
            {
              className: `tier-plate${comp.have ? '' : ' dim'}`,
              style: { background: TIER_COLORS[comp.tier]! },
            },
            [
              el('span', {
                className: `sw ${GEMS[comp.type].shape} mini`,
                style: { background: GEMS[comp.type].color },
              }),
            ],
          ),
        );
      }
      row.append(
        el('span', {
          className: `recipe-count${status.complete ? ' ready' : ''}`,
          text: status.complete ? 'GO' : `${3 - status.missing}/3`,
        }),
      );
      stack.append(row);
    }
    return stack;
  }

  /** Compact hp label: 910044 -> 910k. */
  private static hpLabel(hp: number): string {
    return hp >= 10_000 ? `${Math.round(hp / 1000)}k` : String(hp);
  }

  private renderWavePreview(game: Game): void {
    const box = byId('wavePreview');
    const scroll = box.scrollLeft; // survive the rebuild
    clear(box);
    // One pill per remaining wave of the current 50-block; a wave's pill
    // disappears once it is cleared (game.wave advances). Surviving a block
    // reveals the next one: 1-50, then 51-100, then 101-150, ...
    const blockEnd = Math.ceil(game.wave / LAST_WAVE) * LAST_WAVE;
    for (let n = game.wave; n <= blockEnd; n++) {
      const d = waveDef(n);
      const active = n === game.wave && (game.phase === 'wave' || game.phase === 'prewave');
      const pill = el('span', { className: `wp-pill${active ? ' active' : ''}` }, [
        el('b', { text: `W${n}` }),
        el('span', { text: `♥ ${Panel.hpLabel(d.hp)}`, style: { color: '#ff8c8c' } }),
      ]);
      if (d.armor) pill.append(el('span', { className: 'wp-armor', text: `🛡${d.armor}` }));
      pill.append(...chipsFor(d));
      box.append(pill);
    }
    box.scrollLeft = scroll;
  }

  private renderChances(game: Game): void {
    const box = byId('chances');
    clear(box);
    const table = CHANCE_TABLE[game.chanceLvl]!;
    TIERS.slice(0, 5).forEach((t, i) => {
      box.append(
        el('div', { className: 'chancebar' }, [
          el('span', { className: 'clabel', text: t, style: { color: TIER_COLORS[i]! } }),
          el('div', { className: 'cbar' }, [
            el('div', { style: { width: `${table[i]!}%`, background: TIER_COLORS[i]! } }),
          ]),
          el('span', { className: 'cpct', text: `${table[i]!}%` }),
        ]),
      );
    });
    const up = byId('upChance', HTMLButtonElement);
    if (game.chanceLvl >= CHANCE_COSTS.length) {
      up.textContent = `Lv ${game.chanceLvl + 1} · ✨ Quality maxed`;
      up.disabled = true;
    } else {
      up.textContent = `Lv ${game.chanceLvl + 1} · ⬆ Improve quality · ${CHANCE_COSTS[game.chanceLvl]!}g`;
      up.disabled = game.gold < CHANCE_COSTS[game.chanceLvl]! || game.phase === 'gameover';
    }
  }
}
